/* ----------------------------------------------------------------------------
 * useSuggestWriteVoice — Aura blog writing glue for the voice controller AND the
 * typed chat rail. Two entry points, one view and one confirm loop:
 *   - P2 (owner GO 10-10-2026): "which 3 blogs should we write?" -> 3 cards + a
 *     one-line spoken summary -> "write them" / a pure yes / tap -> 3 drafts.
 *   - Single blog (owner GO 12-10-2026 15:53 UTC, "go blog writing"):
 *     "write a blog for <keyword>" / "about <topic>" -> ONE card + the read-back
 *     "Write a blog for '<keyword>' on <site>?" -> a pure yes / tap -> 1 draft;
 *     "write the blog you suggested" / "the second one" -> that card's read-back.
 * The server is authoritative: it picks the keywords, decides new vs refresh,
 * binds the confirm token to exactly what is shown, enforces owner/operator and the
 * monthly AI writing cap. Writing creates BookedCentral drafts only; the drafts are
 * then handed to the P1 flow (onWritten), whose own rules decide Draft vs Publish.
 *
 * Voice rules: cards are shown, never read out — Aura speaks one line. Sites are
 * resolved ONLY within the VIEWED domain (r2 domain scope). Errors are never
 * swallowed: each failure lands in view.error / the card and is spoken briefly.
 * -------------------------------------------------------------------------- */

import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import type { ConversationTurn } from '../wake/turnEntity.ts';
import {
  BATCH_OUT_OF_DOMAIN,
  batchTargetUnlinkedLine,
  resolveBatchSite,
  type BatchActiveDomain,
  type BatchChannel,
  type BatchSiteOption,
} from './batchPublishModel.ts';
import {
  PICK_ALREADY,
  PICK_WHICH,
  SINGLE_CANCEL_ACK,
  SINGLE_REASK,
  SUGGESTED_NONE,
  WRITE_CANCEL_ACK,
  WRITE_NO_SITE,
  WRITE_OWNER_OPERATOR_ONLY,
  WRITE_PICK_SITE,
  WRITE_REASK,
  WRITE_WHICH,
  WRITING_FORBIDDEN_CODE,
  parseSelection,
  detectPickAsk,
  emptySuggestView,
  matchWriteAnswer,
  resolvePick,
  resolveWriteAnswer,
  singleReadbackLine,
  writeDoneLine,
  type SuggestItemResult,
  type SuggestViewCard,
  type SuggestWriteAsk,
  type SuggestWriteView,
} from './suggestWriteModel.ts';
import { aiLimitFromError, aiLimitFromWrite, limitSpokenLine } from './aiWritingLimit.ts';

export interface SuggestResult {
  kind?: 'suggest' | 'single';
  site: { id: number; label: string; url: string; name: string };
  items: SuggestViewCard[];
  reason: string;
  spoken_summary: string;
  confirm_token?: string;
  write: { available: boolean; reason: string; message?: string; cap?: string; spent?: string; setting?: string };
}

export interface SuggestWriteDeps {
  listSites(): Promise<BatchSiteOption[]>;
  suggest(targetId: number, channel: BatchChannel, viewedProjectId?: number | null): Promise<SuggestResult>;
  proposeOne(targetId: number, keyword: string, channel: BatchChannel, viewedProjectId?: number | null): Promise<SuggestResult>;
  write(token: string, targetId: number, index: number, keyword: string): Promise<{ status: string; line: string; draft_id: number | null; reason: string }>;
}

export interface UseSuggestWriteVoiceArgs {
  deps: SuggestWriteDeps;
  speak: (text: string) => Promise<void>;
  turnRef: MutableRefObject<ConversationTurn>;
  listenRef: MutableRefObject<(() => Promise<unknown>) | null>;
  /** The domain being VIEWED ("Viewing: <domain>" header; r2 domain scope). Sites are
   *  resolved ONLY within it, exactly like the P1 batch flow. A bare project id is
   *  accepted (legacy callers). */
  activeDomain?: () => BatchActiveDomain | number | null;
  /** Owner GO 12-10-2026: writing is owner/operator only. Mirrors the server (which
   *  stays the authority and answers 403). Absent = allowed (the server decides). */
  canWrite?: () => boolean;
  /** hand-off to the P1 Draft/Publish cards once drafts exist. */
  onWritten?: (site: { id: number; name: string; label: string; url: string; projectId: number | null }, draftIds: number[], channel: BatchChannel) => Promise<void> | void;
}

export interface SuggestWriteVoice {
  view: SuggestWriteView | null;
  begin: (ask: SuggestWriteAsk, channel: BatchChannel) => Promise<void>;
  handleAnswer: (answer: string, channel: BatchChannel) => Promise<void>;
  writeAll: (channel?: BatchChannel) => Promise<void>;
  /** tap "Write this one" on a card: the tap IS the confirmation. */
  writeOne: (index: number, channel?: BatchChannel) => Promise<void>;
  /** conversational-actions: write the cards the user picked from Aura's recommend list
   *  (the suggestion was loaded there); same runWrites, cap check and hand-off. */
  adoptAndWrite: (r: SuggestResult, site: BatchSiteOption, indexes: number[], channel: BatchChannel) => Promise<void>;
  chooseSite: (siteId: number) => Promise<void>;
  dismiss: () => void;
  isAwaitingAnswer: () => boolean;
}

function errCode(e: unknown): string {
  return String((e as { body?: { error?: { code?: string } } })?.body?.error?.code ?? (e as { code?: string })?.code ?? '');
}
function errMessage(e: unknown): string {
  const m = (e as { body?: { error?: { message?: string } } })?.body?.error?.message;
  if (typeof m === 'string' && m !== '') return m;
  const c = errCode(e);
  return c ? 'That didn’t go through (' + c.replace(/_/g, ' ') + ').' : 'That didn’t go through. Please try again.';
}
const STOP_CODES = new Set(['ai_budget_reached', 'ai_metering_unavailable', 'ai_model_unpriced', WRITING_FORBIDDEN_CODE]);

export function useSuggestWriteVoice(args: UseSuggestWriteVoiceArgs): SuggestWriteVoice {
  const { deps, speak, turnRef, listenRef } = args;
  const [view, setView] = useState<SuggestWriteView | null>(null);
  const viewRef = useRef<SuggestWriteView | null>(null);
  /** the single-use confirm token: memory only, never rendered, logged or stored. */
  const tokenRef = useRef<string | null>(null);
  const set = useCallback((next: SuggestWriteView | null) => { viewRef.current = next; setView(next); }, []);
  const patch = useCallback((p: Partial<SuggestWriteView>) => {
    if (!viewRef.current) return;
    set({ ...viewRef.current, ...p });
  }, [set]);

  const say = useCallback(async (line: string) => {
    try { await speak(line); } catch (e) {
      patch({ error: 'Aura could not speak just now — the details are on screen.' });
      void e;
    }
  }, [speak, patch]);

  const viewedProjectId = useCallback((): number | null => {
    const a = args.activeDomain ? args.activeDomain() : null;
    if (a == null) return null;
    return typeof a === 'number' ? a : (a.projectId ?? null);
  }, [args]);

  /** one brief line, nothing actionable (no cards, no buttons, no open question). */
  const refuse = useCallback(async (channel: BatchChannel) => {
    turnRef.current.clearSubStateIfKind('suggest_write');
    tokenRef.current = null;
    set({ ...emptySuggestView(channel), phase: 'none', line: WRITE_OWNER_OPERATOR_ONLY });
    await say(WRITE_OWNER_OPERATOR_ONLY);
  }, [set, say, turnRef]);

  /** a server refusal at propose/suggest time -> the right one-line answer. */
  const loadFailed = useCallback(async (e: unknown, site: BatchSiteOption, channel: BatchChannel, what: string) => {
    const code = errCode(e);
    if (code === WRITING_FORBIDDEN_CODE) { await refuse(channel); return; }
    const msg = code === 'target_not_in_viewed_domain' ? BATCH_OUT_OF_DOMAIN
      : code === 'target_not_associated_with_domain' ? batchTargetUnlinkedLine(site.name)
      : errMessage(e);
    patch({ phase: 'error', error: msg, line: 'I couldn’t load ' + what + ' for ' + site.name + '.' });
    await say(msg);
  }, [patch, say, refuse]);

  /** shared tail of suggest + propose-one: show, speak ONE line, open the question. */
  const present = useCallback(async (r: SuggestResult, site: BatchSiteOption, channel: BatchChannel, kind: 'suggest' | 'single') => {
    if (!r.items || r.items.length === 0) {
      patch({ phase: 'none', kind, items: [], line: r.spoken_summary });
      await say(r.spoken_summary);
      return;
    }
    const canWrite = Boolean(r.write?.available && r.confirm_token);
    tokenRef.current = canWrite ? (r.confirm_token as string) : null;
    const limit = aiLimitFromWrite(r.write);
    patch({
      phase: canWrite ? 'asking' : 'limit', kind, pick: kind === 'single' ? r.items[0].index : null, items: r.items, line: r.spoken_summary,
      writeAvailable: canWrite, limitMessage: limit ? '' : (r.write?.message ?? ''), limit, results: {}, pendingKeyword: null,
    });
    if (canWrite) turnRef.current.setSuggestWrite(site.id, channel, kind === 'single' ? r.items[0].index : null);
    await say(r.spoken_summary); // ONE line. The cards carry the detail.
    if (canWrite && channel === 'voice' && turnRef.current.subState?.kind === 'suggest_write') {
      await listenRef.current?.();
    }
  }, [patch, say, turnRef, listenRef]);

  const loadSuggestions = useCallback(async (site: BatchSiteOption, channel: BatchChannel) => {
    tokenRef.current = null;
    patch({ phase: 'loading', kind: 'suggest', site: { id: site.id, name: site.name, label: site.label, url: site.url, projectId: site.projectId }, sites: [], line: 'Looking at your top keywords for ' + site.name + '…' });
    let r: SuggestResult;
    try {
      r = await deps.suggest(site.id, channel, viewedProjectId());
    } catch (e) {
      await loadFailed(e, site, channel, 'suggestions');
      return;
    }
    await present(r, site, channel, 'suggest');
  }, [deps, patch, present, loadFailed, viewedProjectId]);

  const loadSingle = useCallback(async (site: BatchSiteOption, keyword: string, channel: BatchChannel) => {
    tokenRef.current = null;
    patch({ phase: 'loading', kind: 'single', site: { id: site.id, name: site.name, label: site.label, url: site.url, projectId: site.projectId }, sites: [], line: 'Preparing a blog for “' + keyword + '” on ' + site.name + '…' });
    let r: SuggestResult;
    try {
      r = await deps.proposeOne(site.id, keyword, channel, viewedProjectId());
    } catch (e) {
      await loadFailed(e, site, channel, 'that blog');
      return;
    }
    await present(r, site, channel, 'single');
  }, [deps, patch, present, loadFailed, viewedProjectId]);

  /** a card already on screen ("write the second one" / "the one you suggested"). */
  const pickCard = useCallback(async (pick: { index: number | null; about: string | null }, channel: BatchChannel) => {
    const v = viewRef.current;
    if (!v || v.kind !== 'suggest' || v.items.length === 0 || !v.site || !tokenRef.current) {
      await say(SUGGESTED_NONE);
      return;
    }
    const idx = resolvePick(pick, v.items);
    if (idx === 'none') { await say(SUGGESTED_NONE); return; }
    if (idx === 'ambiguous') {
      patch({ line: PICK_WHICH });
      await say(PICK_WHICH);
      if (channel === 'voice' && turnRef.current.subState?.kind === 'suggest_write') await listenRef.current?.();
      return;
    }
    if (v.results[idx]?.status === 'written') { await say(PICK_ALREADY); return; }
    const card = v.items.find((c) => c.index === idx);
    if (!card) { await say(PICK_WHICH); return; }
    const line = singleReadbackLine(card.keyword, v.site.name);
    patch({ pick: idx, phase: 'asking', line });
    turnRef.current.setSuggestWrite(v.site.id, channel, idx);
    await say(line);
    if (channel === 'voice' && turnRef.current.subState?.kind === 'suggest_write') await listenRef.current?.();
  }, [patch, say, turnRef, listenRef]);

  const begin = useCallback(async (ask: SuggestWriteAsk, channel: BatchChannel) => {
    if (args.canWrite && !args.canWrite()) { await refuse(channel); return; }
    if (ask.pick) { await pickCard(ask.pick, channel); return; }
    tokenRef.current = null;
    const kind: 'suggest' | 'single' = ask.single ? 'single' : 'suggest';
    set({ ...emptySuggestView(channel), kind, pendingKeyword: ask.single?.keyword ?? null, line: 'Finding your connected sites…' });
    let sites: BatchSiteOption[];
    try {
      sites = await deps.listSites();
    } catch (e) {
      patch({ phase: 'error', error: errMessage(e), line: WRITE_NO_SITE });
      await say(WRITE_NO_SITE);
      return;
    }
    const res = resolveBatchSite(sites, ask.siteHint, args.activeDomain ? args.activeDomain() : null);
    if (res.kind === 'out_of_domain') {
      patch({ phase: 'none', line: BATCH_OUT_OF_DOMAIN });
      await say(BATCH_OUT_OF_DOMAIN);
      return;
    }
    if (res.kind === 'none') {
      patch({ phase: 'none', line: WRITE_NO_SITE });
      await say(WRITE_NO_SITE);
      return;
    }
    if (res.kind === 'choose') {
      // Never guess between two sites (e.g. booksesh.com root vs /partners).
      patch({ phase: 'choose_site', sites: res.options, line: WRITE_PICK_SITE });
      await say(WRITE_PICK_SITE);
      return;
    }
    if (ask.single) await loadSingle(res.site, ask.single.keyword, channel);
    else await loadSuggestions(res.site, channel);
  }, [deps, set, patch, say, loadSuggestions, loadSingle, pickCard, refuse, args]);

  const chooseSite = useCallback(async (siteId: number) => {
    const v = viewRef.current;
    const site = v?.sites.find((s) => s.id === siteId);
    if (!v || !site) return;
    const ch = v.channel === 'voice' ? 'voice' : v.channel;
    if (v.kind === 'single' && v.pendingKeyword) await loadSingle(site, v.pendingKeyword, ch);
    else await loadSuggestions(site, ch);
  }, [loadSuggestions, loadSingle]);

  /** write the given cards (all, or one), then hand the drafts to P1. */
  const runWrites = useCallback(async (only: number[] | null, channel: BatchChannel) => {
    const v = viewRef.current;
    const token = tokenRef.current;
    if (!v || !v.site || !token || v.phase === 'writing') return;
    if (args.canWrite && !args.canWrite()) { await refuse(channel); return; }
    turnRef.current.clearSubStateIfKind('suggest_write');
    const site = v.site;
    const cards = v.items.filter((c) => (only === null ? true : only.includes(c.index)) && v.results[c.index]?.status !== 'written');
    if (cards.length === 0) { await say(PICK_ALREADY); return; }
    let results: Record<number, SuggestItemResult> = { ...v.results };
    patch({ phase: 'writing', error: '', resultLine: '', results, line: (cards.length === 1 ? 'Writing the draft' : 'Writing ' + cards.length + ' drafts') + ' for ' + site.name + '… this can take a minute each.' });
    const written: number[] = [];
    let stopMessage = '';
    let forbidden = false;
    for (const c of cards) {
      if (stopMessage !== '') {
        results = { ...results, [c.index]: { status: 'skipped', line: 'Not written (limit reached).', draft_id: null } };
        patch({ results });
        continue;
      }
      results = { ...results, [c.index]: { status: 'writing', line: 'Writing…', draft_id: null } };
      patch({ results });
      try {
        const w = await deps.write(token, site.id, c.index, c.keyword);
        results = { ...results, [c.index]: { status: w.status, line: w.line, draft_id: w.draft_id } };
        if (w.status === 'written' && w.draft_id) written.push(w.draft_id);
      } catch (e) {
        const code = errCode(e);
        const msg = code === WRITING_FORBIDDEN_CODE ? WRITE_OWNER_OPERATOR_ONLY : errMessage(e);
        results = { ...results, [c.index]: { status: code === 'ai_budget_reached' ? 'refused' : 'failed', line: msg, draft_id: null } };
        const limit = aiLimitFromError(e);
        if (limit) patch({ limit }); // the clear on-screen warning (limit, used, how to raise)
        if (STOP_CODES.has(code)) { stopMessage = limit ? limitSpokenLine(limit) : msg; forbidden = code === WRITING_FORBIDDEN_CODE; }
      }
      patch({ results });
    }
    // a suggestion keeps its token for the cards not yet written (a pick may follow)
    const remaining = v.kind === 'suggest' && v.items.some((c) => results[c.index]?.status !== 'written' && results[c.index]?.status !== 'duplicate');
    if (!remaining || stopMessage !== '') tokenRef.current = null;
    const other = cards.length - written.length;
    const done = forbidden ? WRITE_OWNER_OPERATOR_ONLY
      : stopMessage !== '' ? stopMessage + ' ' + writeDoneLine(written.length, 0) : writeDoneLine(written.length, other);
    patch({ phase: remaining && stopMessage === '' ? 'asking' : 'done', pick: null, resultLine: done, writeAvailable: remaining && stopMessage === '' });
    await say(done);
    if (written.length > 0 && args.onWritten) {
      // Hand-off: the P1 cards (Draft / Publish, default draft, per-site policy,
      // WordPress opt-in, server read-back) take over from here.
      await args.onWritten(site, written, channel);
    }
  }, [deps, patch, say, turnRef, args, refuse]);

  const writeAll = useCallback(async (channel: BatchChannel = 'tap') => {
    await runWrites(null, channel);
  }, [runWrites]);

  const writeOne = useCallback(async (index: number, channel: BatchChannel = 'tap') => {
    await runWrites([index], channel);
  }, [runWrites]);

  const adoptAndWrite = useCallback(async (r: SuggestResult, site: BatchSiteOption, indexes: number[], channel: BatchChannel) => {
    if (args.canWrite && !args.canWrite()) { await refuse(channel); return; }
    const canWrite = Boolean(r.write?.available && r.confirm_token);
    tokenRef.current = canWrite ? (r.confirm_token as string) : null;
    const limit = aiLimitFromWrite(r.write);
    const blocked = limit ? limitSpokenLine(limit) : (r.write?.message || 'Writing isn’t available right now.');
    set({
      ...emptySuggestView(channel), kind: 'suggest', phase: canWrite ? 'asking' : 'limit', items: r.items ?? [], line: canWrite ? '' : blocked,
      site: { id: site.id, name: site.name, label: site.label, url: site.url, projectId: site.projectId },
      writeAvailable: canWrite, limit, limitMessage: limit ? '' : (r.write?.message ?? ''),
    });
    if (!canWrite) { await say(blocked); return; }
    await runWrites(indexes, channel);
  }, [args, refuse, set, say, runWrites]);

  const handleAnswer = useCallback(async (answer: string, channel: BatchChannel) => {
    const ctx = turnRef.current.subState;
    if (!ctx || ctx.kind !== 'suggest_write') return;
    const v = viewRef.current;
    const open = v && v.kind === 'suggest' && ctx.pick === null
      ? v.items.filter((c) => v.results[c.index]?.status !== 'written' && v.results[c.index]?.status !== 'duplicate')
      : [];
    if (open.length > 1 && matchWriteAnswer(answer).kind !== 'no') {
      // Several cards open (owner 16:48 UTC): the user must say which ones — or all.
      const sel = parseSelection(answer, open.length);
      if (sel === 'all') { await runWrites(open.map((c) => c.index), channel); return; }
      if (sel && (sel.length > 1 || ctx.selecting)) { await runWrites(sel.map((i) => open[i].index), channel); return; }
      if (sel && sel.length === 1) { await pickCard({ index: sel[0], about: null }, channel); return; }
      // "write the one about X" / "that one" -> that card's read-back.
      const pick = detectPickAsk(answer);
      if (pick) { await pickCard(pick, channel); return; }
      if (matchWriteAnswer(answer).kind === 'write' || ctx.selecting) {
        // a bare yes / "write it" / "write them" names no card -> ask which (once).
        if (ctx.selecting && ctx.reAskCount >= 1) {
          turnRef.current.clearSubStateIfKind('suggest_write');
          patch({ phase: 'cancelled', line: WRITE_CANCEL_ACK });
          await say(WRITE_CANCEL_ACK);
          return;
        }
        if (ctx.selecting) turnRef.current.incReask();
        turnRef.current.markSuggestSelecting();
        patch({ line: WRITE_WHICH });
        await say(WRITE_WHICH);
        if (channel === 'voice' && turnRef.current.subState?.kind === 'suggest_write') await listenRef.current?.();
        return;
      }
    }
    const single = ctx.pick !== null && ctx.pick !== undefined;
    const step = resolveWriteAnswer(matchWriteAnswer(answer), ctx.reAskCount);
    if (step.kind === 'write') {
      await runWrites(single ? [ctx.pick as number] : null, channel);
      return;
    }
    if (step.kind === 're-ask') {
      turnRef.current.incReask();
      const line = single ? SINGLE_REASK : WRITE_REASK;
      patch({ line });
      await say(line);
      if (channel === 'voice' && turnRef.current.subState?.kind === 'suggest_write') await listenRef.current?.();
      return;
    }
    turnRef.current.clearSubStateIfKind('suggest_write');
    tokenRef.current = null;
    const ack = single ? SINGLE_CANCEL_ACK : WRITE_CANCEL_ACK;
    patch({ phase: 'cancelled', pick: null, line: ack });
    await say(ack);
  }, [runWrites, pickCard, patch, say, turnRef, listenRef]);

  const dismiss = useCallback(() => {
    turnRef.current.clearSubStateIfKind('suggest_write');
    tokenRef.current = null;
    set(null);
  }, [set, turnRef]);

  const isAwaitingAnswer = useCallback(() => turnRef.current.subState?.kind === 'suggest_write', [turnRef]);

  return { view, begin, handleAnswer, writeAll, writeOne, adoptAndWrite, chooseSite, dismiss, isAwaitingAnswer };
}
